import React, { useMemo } from "react";
import { Col, Modal, DatePicker, Divider, Form, Input, Row, Select, Spin, Tooltip } from "antd";
import type { Moment } from "moment";
import { useOfferContext } from "../../../containers/OfferContainer/context/OfferContext";
import { useAppSelector } from "../../..";
import { ReactComponent as CancelButtonIcon } from "../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../img/buttons_icons/submitButton_icon.svg";
import { checkIsLoading, displayFormat, getSelectOptions } from "../../../lib/utils";
import type {
  ICreateMode,
  IServiceProfile,
  IServiceProfileFieldName,
  IViewMode,
  Offer,
} from "../../../lib/types";
import { isFieldAvailable } from "./utils/isFieldAvailable";
import { isFieldRequired } from "./utils/isFieldRequired";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import "./ServiceProfileEdit.less";

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
  className: "form-item-required__hidden-error__mb0",
};

const rowLayout = {
  gutter: 90,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  clearServiceProfile: () => void;
  viewMode: Exclude<IViewMode, "view"> | ICreateMode;
  onCancel?: () => void;
  onSubmit?: () => void;
  isPacketTariff?: boolean; // Используется только в режиме создания
  isCommonOfferingType?: boolean; // Используется только в режиме создания
  serviceProfile?: IServiceProfile;
  offer?: Offer;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ServiceProfileEdit = (props: ComponentProps) => {
  const {
    clearServiceProfile,
    viewMode,
    onCancel,
    onSubmit,
    // creatingFormsData,
    isCommonOfferingType,
    isPacketTariff,
    serviceProfile,
    offer,
  } = props;
  const { serviceProfileForm } = useOfferContext();
  const { billPeriods, billPeriodsLoading } = useAppSelector((state) => state.dicts);
  const {
    paymentType,
    paymentTypeLoading,
    profileTypeStatus: profileTypes,
    profileTypeStatusLoading: profileTypesLoading,
    invoicePeriodStatus: invoicePeriods,
    invoicePeriodStatusLoading: invoicePeriodsLoading,
    prePaymentPeriodStatus: prepaymentPeriods,
    prePaymentPeriodStatusLoading: prepaymentPeriodsLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const {
    serviceProfiles: commonServiceProfiles,
    serviceProfilesLoading: commonServiceProfilesLoading,
  } = useAppSelector((state) => state.serviceProfile);

  const { serviceProfileLoading } = useAppSelector((state) => state.offering);

  /* Расчетный период: для БМ "Пакетный" доступен только "месяц".
     Работает только в режиме создания. Для режима редактирования нужно будет дорабатывать,
     т.к. пока не понятна взаимосвязь разделов тарификация и профиля оплаты
     !!!Учесть, что в редактировании creatingFormsData не используется, есть только данные формы
   */
  const allowedInvoicePeriods = useMemo(
    () =>
      // creatingFormsData?.tariffication.tariffModelId === 2
      isPacketTariff ? invoicePeriods.filter(({ id }) => id === 1) : invoicePeriods,
    // [invoicePeriods, creatingFormsData?.tariffication.tariffModelId]
    [invoicePeriods, isPacketTariff]
  );

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onCancel && onCancel();
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmit && onSubmit();
  };

  // Для типа тарифа пакетный, доступны только профили с invoicePeriodId: 1
  const filteredСommonServiceProfiles = useMemo(() => {
    // if (creatingFormsData?.tariffication.tariffModelId === 2) {
    if (isPacketTariff) {
      return commonServiceProfiles.filter(({ invoicePeriodId }) => invoicePeriodId === 1);
    }
    return commonServiceProfiles;
    // }, [commonServiceProfiles, creatingFormsData?.tariffication.tariffModelId]);
  }, [commonServiceProfiles, isPacketTariff]);

  const isCommonType = useMemo(() => {
    if (viewMode === "create") {
      // return creatingFormsData?.offer.offeringTypeId === 1;
      return isCommonOfferingType;
    }
    return serviceProfile?.profileTypeId === 1;
    // }, [viewMode, creatingFormsData, serviceProfile]);
  }, [viewMode, isCommonOfferingType, serviceProfile]);

  const onSelectId = (v, data) => {
    const { profileTypeId, fromDate, toDate, ...fieldsValues } = data.option;
    if (!isCommonType) {
      Modal.info({
        content: "Профиль изменен на индивидуальный",
        okButtonProps: {
          style: {
            width: "108px",
          },
        },
      });
    }
    serviceProfileForm.setFieldsValue(fieldsValues);
  };

  const offeringStatusId = useMemo(() => {
    if (viewMode === "create") return OFFER_STATUSES.PREPARE;
    return offer?.offeringStatusId;
  }, [viewMode, offer]);

  const getRequired = (fieldName: IServiceProfileFieldName): boolean => {
    if (offeringStatusId) {
      return isFieldRequired({
        fieldName,
        viewMode,
        offeringStatusId,
        options: { isCommonType },
      });
    }
    return false;
  };

  const getAvailable = (fieldName: IServiceProfileFieldName): boolean => {
    // console.log({
    //   fieldName,
    //   viewMode,
    //   offeringStatusId,
    //   options: { isCommonType },
    // });
    if (offeringStatusId) {
      return isFieldAvailable({
        fieldName,
        viewMode,
        offeringStatusId,
        options: { isCommonType },
      });
    }
    return false;
  };

  const disableFromDate = (date: Moment): boolean => {
    const isValidByProfileDates = date.isBetween(
      serviceProfile?.fromDate,
      serviceProfile?.toDate,
      "day",
      "(]"
    );
    const isValidByOfferDates = date.isBetween(offer?.fromDate, offer?.toDate, "day", "(]");
    return !isValidByProfileDates || !isValidByOfferDates;
  };

  const renderButtons = () =>
    viewMode !== "create" ? (
      <Row align="middle" className="serviceProfile-edit__btns">
        <Col>
          <Tooltip title="Подтвердить">
            <SubmitButtonIcon onClick={handleOnSubmit} />
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Отменить">
            <CancelButtonIcon onClick={handleOnCancel} />
          </Tooltip>
        </Col>
      </Row>
    ) : null;

  return (
    <div className="serviceProfile-edit">
      <Divider style={{ margin: "0 0 9px" }} />
      {/* <Button
        onClick={() => {
          console.log(serviceProfileForm.getFieldsValue());
        }}
      >
        form values
      </Button> */}
      <Spin spinning={checkIsLoading(serviceProfileLoading)}>
        {renderButtons()}
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Профиль"
              name="id"
              {...formItemLayout}
              rules={[{ required: getRequired("id"), message: "" }]}
            >
              <Select
                style={isCommonType ? {} : { color: "white" }} // Для индивидуальных нужно название общего профила "стирать"
                options={getSelectOptions({
                  list: filteredСommonServiceProfiles,
                })}
                allowClear
                loading={checkIsLoading(commonServiceProfilesLoading)}
                onSelect={onSelectId}
                onClear={clearServiceProfile}
                disabled={!getAvailable("id")}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Тип профиля"
              name="profileTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("profileTypeId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("profileTypeId")}
                showArrow={false}
                options={getSelectOptions({
                  list: profileTypes,
                })}
                loading={checkIsLoading(profileTypesLoading)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Тип оплаты"
              name="paymentTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("paymentTypeId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("paymentTypeId")}
                showArrow={getAvailable("paymentTypeId")}
                options={getSelectOptions({
                  list: paymentType,
                })}
                allowClear
                loading={checkIsLoading(paymentTypeLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Срок на предоплату (дн)"
              name="prepaymentToPayDays"
              {...formItemLayout}
              rules={[
                { required: getRequired("prepaymentToPayDays"), message: "" },
                {
                  validator: (rule, value) => {
                    if (Number(value) < 1) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: "",
                },
              ]}
            >
              <Input type="number" allowClear disabled={!getAvailable("prepaymentToPayDays")} />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Расчетный период"
              name="invoicePeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("invoicePeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("prepaymentToPayDays")}
                showArrow={getAvailable("prepaymentToPayDays")}
                options={getSelectOptions({
                  list: allowedInvoicePeriods,
                })}
                allowClear
                loading={checkIsLoading(invoicePeriodsLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Срок на постоплату (дн)"
              name="billToPayDays"
              {...formItemLayout}
              rules={[
                { required: getRequired("billToPayDays"), message: "" },
                {
                  validator: (rule, value) => {
                    if (Number(value) < 1) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: "",
                },
              ]}
            >
              <Input type="number" allowClear disabled={!getAvailable("billToPayDays")} />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={10}>
            <Form.Item
              label="Период счета на предоплату"
              name="prepaymentPeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("prepaymentPeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("prepaymentPeriodId")}
                showArrow={getAvailable("prepaymentPeriodId")}
                options={getSelectOptions({
                  list: prepaymentPeriods,
                })}
                allowClear
                loading={checkIsLoading(prepaymentPeriodsLoading)}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Период счета на постоплату"
              name="billPeriodId"
              {...formItemLayout}
              rules={[{ required: getRequired("billPeriodId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("billPeriodId")}
                showArrow={getAvailable("billPeriodId")}
                options={getSelectOptions({
                  list: billPeriods,
                })}
                allowClear
                loading={checkIsLoading(billPeriodsLoading)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          {viewMode !== "create" && (
            <Col span={10} offset={10}>
              <Row>
                <Col span={formItemLayout.labelCol.span}>
                  <Form.Item
                    name="fromDate"
                    label="Период"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("fromDate"), message: "" }]}
                  >
                    <></>
                  </Form.Item>
                </Col>
                <Col span={formItemLayout.wrapperCol.span / 2 - 1}>
                  <Form.Item
                    name="fromDate"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("fromDate"), message: "" }]}
                  >
                    <DatePicker
                      format={displayFormat}
                      disabledDate={disableFromDate}
                      disabled={!getAvailable("fromDate")}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <div className="tarification-params-edit__periodSeparator">-</div>
                </Col>
                <Col span={formItemLayout.wrapperCol.span / 2 - 1}>
                  <Form.Item
                    name="toDate"
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: getRequired("toDate"), message: "" }]}
                  >
                    <DatePicker
                      format={displayFormat}
                      disabledDate={disableFromDate}
                      disabled={!getAvailable("toDate")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Spin>
    </div>
  );
};
